/**
 * Same-page channel for "open/collapse the left sidebar" requests.
 *
 * The sidebar's open state lives in the shared `sidebar_open` cookie
 * (ThemedOadsSidebar, PAD-31716), but `cookie-state` hook instances do not
 * notice each other's writes — so a component elsewhere on the page (the
 * Copilot chat, which collapses the menu when it opens) cannot move the
 * mounted sidebar by writing the cookie alone. This bus carries the request to
 * whichever sidebar is mounted; the sidebar then writes the cookie itself, so
 * the next page (and platform-next, same cookie domain) agrees.
 *
 * Dependency-free ESM so scripts/test-copilot-sidebar-collapse.mjs can run the
 * real source under node:test.
 */
export const SIDEBAR_OPEN_EVENT = "oppizi:sidebar-open";

/**
 * A request made just before the sidebar subscribed is replayed to it. On a
 * `?copilot=open` landing the chat's deep-link effect fires in the same commit
 * as — and before — the sidebar's subscribe effect, so the event would be
 * dropped and the menu stay expanded on that page. The window is deliberately
 * short: a sidebar that mounts later (a route change) must not inherit a
 * collapse the user has since undone.
 */
export const REPLAY_WINDOW_MS = 1000;

let lastRequest = null;

/** Ask the mounted sidebar to open (true) or collapse (false). No-op outside a browser. */
export const requestSidebarOpen = (open, now = Date.now) => {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return false;
  lastRequest = { open: Boolean(open), at: now() };
  window.dispatchEvent(new CustomEvent(SIDEBAR_OPEN_EVENT, { detail: { open: Boolean(open) } }));
  return true;
};

/**
 * Subscribe to sidebar open requests; a request made within REPLAY_WINDOW_MS
 * before subscribing is replayed first. Returns the unsubscribe function.
 * Malformed events (no boolean `detail.open`) are ignored.
 */
export const subscribeSidebarOpen = (handler, now = Date.now) => {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return () => {};
  const listener = (event) => {
    const open = event && event.detail ? event.detail.open : undefined;
    if (typeof open === "boolean") handler(open);
  };
  window.addEventListener(SIDEBAR_OPEN_EVENT, listener);
  if (lastRequest && now() - lastRequest.at <= REPLAY_WINDOW_MS) handler(lastRequest.open);
  return () => window.removeEventListener(SIDEBAR_OPEN_EVENT, listener);
};
